import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ModalWrapper from '../../../ModalWrapper';
import { AutoSuggest, CommonFooter } from '../../../FormComponent';
import { DO_NOTHING, debounce } from '../../../../utils/commonfunctions';
import { fetchAllTestCaseData, updateAutomationInstruction, updateExecutionRecord, saveAutomationInstructionOnly } from '../../../../utils/api';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { CheckCircle } from '@mui/icons-material';
import styles from '../../../../styles/automation/popup.module.scss';
import QuickCreateTestCaseModal from './QuickCreateTestCaseModal';
import { AutomationStatusEnum } from '../../../../enums/AutomationStatusEnum';
import {
    SAVE_EXECUTION_RECORD_COPY,
    SAVE_EXECUTION_RECORD_OVERWRITE_WARNING,
    SAVE_EXECUTION_RECORD_VARIANT,
} from '../../content/saveExecutionRecord.constants';

export default function SaveExecutionRecord({
    onClose = DO_NOTHING,
    popupData = {},
    onSave,
    testCaseId,
    selectedTestCase,
    onTestCaseChange,
    options = [],
    loading = false,
    apiError = null,
    warningNote = false,
    isSaving = false,
    disabled = false,
    onInputChange,
    onDropdownScroll,
    onKeyboardNavigation,
    getOptionLabel,
    getOptionValue,
    isOptionEqualToValue,
    filterOptions,
    renderOption,
    getNoOptionsText,
    createNewValue,
    onCreateNew,
    showCreateModal = false,
    onCreateModalClose,
    createModalProps = {},
}) {
    const legacyTestCaseId = testCaseId ?? popupData?.testCaseId ?? '';
    const variant = popupData?.variant ?? SAVE_EXECUTION_RECORD_VARIANT.DEFAULT;
    const copy =
        SAVE_EXECUTION_RECORD_COPY[variant] ??
        SAVE_EXECUTION_RECORD_COPY[SAVE_EXECUTION_RECORD_VARIANT.DEFAULT];
    const isInstructionsOnly = variant === SAVE_EXECUTION_RECORD_VARIANT.INSTRUCTIONS_ONLY;
    const [internalSelectedTestCase, setInternalSelectedTestCase] = useState(
        selectedTestCase ?? legacyTestCaseId ?? null
    );
    const [internalOptions, setInternalOptions] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalApiError, setInternalApiError] = useState(null);
    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const [internalHasMore, setInternalHasMore] = useState(false);
    const [internalPage, setInternalPage] = useState(1);
    const [internalSaving, setInternalSaving] = useState(false);
    const [internalCreateOpen, setInternalCreateOpen] = useState(false);
    const abortControllerRef = useRef(null);

    const isControlled = Boolean(onTestCaseChange || onSave || options.length > 0);
    const effectiveSelectedTestCase = selectedTestCase ?? internalSelectedTestCase;
    const effectiveOptions = options.length > 0 ? options : internalOptions;
    const effectiveLoading = loading || internalLoading;
    const effectiveApiError = apiError ?? internalApiError;
    const effectiveSaving = isSaving || internalSaving;
    const effectiveCreateModalOpen = showCreateModal || internalCreateOpen;
    const CREATE_NEW_VALUE = createNewValue ?? 'CREATE_NEW';

    const internalSearch = useMemo(
        () => debounce(async (term, page = 1, append = false) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            setInternalLoading(true);
            setInternalApiError(null);
            if (!append) setInternalOptions([]);

            try {
                const searchParams = { page, limit: 15, signal };
                if (term?.trim()) {
                    searchParams.testCaseTitle = term.trim();
                    searchParams.testCaseName = term.trim();
                }
                if (legacyTestCaseId) searchParams.testCaseId = legacyTestCaseId;

                const response = await fetchAllTestCaseData(searchParams);
                if (response.status !== 1 || !response.data) {
                    setInternalOptions([]);
                    setInternalHasMore(false);
                    setInternalApiError(response.error || 'Failed to fetch test cases');
                    return;
                }

                let newOptions = [];
                let pagination = {};
                if (response.data.success && response.data.data) {
                    newOptions = response.data.data.results || [];
                    pagination = response.data.data.pagination || {};
                } else if (response.data.results) {
                    newOptions = response.data.results || [];
                    pagination = response.data.pagination || {};
                } else if (Array.isArray(response.data)) {
                    newOptions = response.data;
                }

                setInternalOptions((prev) => (append ? [...prev, ...newOptions] : newOptions));
                setInternalHasMore(Boolean(pagination.hasMore));
                setInternalPage(page);
            } catch (error) {
                if (error.name === 'AbortError' || error.name === 'CanceledError') return;
                setInternalOptions([]);
                setInternalHasMore(false);
                setInternalApiError(error.response?.data?.message || error.message || 'Error searching test cases');
            } finally {
                setInternalLoading(false);
            }
        }, 300),
        [legacyTestCaseId]
    );

    useEffect(() => {
        if (isControlled) return undefined;
        internalSearch('', 1, false);
        return () => {
            abortControllerRef.current?.abort();
            internalSearch.cancel?.();
        };
    }, [internalSearch, isControlled]);

    useEffect(() => {
        if (selectedTestCase !== undefined) {
            setInternalSelectedTestCase(selectedTestCase || null);
        }
    }, [selectedTestCase]);

    const handleInternalInputChange = useCallback((inputValue) => {
        setInternalSearchTerm(inputValue || '');
        setInternalPage(1);
        setInternalHasMore(false);
        internalSearch(inputValue || '', 1, false);
    }, [internalSearch]);

    const loadMoreInternal = useCallback(() => {
        if (internalHasMore && !internalLoading) {
            internalSearch(internalSearchTerm, internalPage + 1, true);
        }
    }, [internalHasMore, internalLoading, internalSearch, internalSearchTerm, internalPage]);

    const handleInternalDropdownScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight * 1.5) loadMoreInternal();
    }, [loadMoreInternal]);

    const handleInternalKeyboardNavigation = useCallback((direction, currentIndex, totalOptions) => {
        if (direction === 'down' && currentIndex >= totalOptions - 3) {
            loadMoreInternal();
        }
    }, [loadMoreInternal]);

    const handleSave = useCallback(async () => {
        if (onSave) {
            await onSave();
            return;
        }

        if (!effectiveSelectedTestCase) return;

        const instructionPayload = (popupData?.instruction || []).filter(
            (inst) => inst?.text?.trim() !== ''
        );

        setInternalSaving(true);
        try {
            if (isInstructionsOnly) {
                const result = await saveAutomationInstructionOnly({
                    testCaseId: effectiveSelectedTestCase,
                    instruction: instructionPayload,
                });
                if (result?.status !== 1) {
                    setInternalApiError(result?.error || 'Failed to save instructions');
                    return;
                }
                await popupData?.onSuccess?.(effectiveSelectedTestCase);
                onClose();
                return;
            }

            await updateAutomationInstruction({
                testCaseId: effectiveSelectedTestCase,
                instruction: instructionPayload,
            });
            const result = await updateExecutionRecord({
                testCaseId: effectiveSelectedTestCase,
                plan: popupData?.plan,
                dynamicResult:
                    popupData?.plan?.metadata?.dynamicResult ||
                    popupData?.plan?.dynamicResults,
            });
            if (result?.status !== 1) {
                setInternalApiError(result?.error || 'Failed to save execution record');
                return;
            }
            await popupData?.onSuccess?.(effectiveSelectedTestCase);
            onClose();
        } catch (error) {
            setInternalApiError(
                error.message ||
                    (isInstructionsOnly ? 'Failed to save instructions' : 'Failed to save execution record')
            );
        } finally {
            setInternalSaving(false);
        }
    }, [effectiveSelectedTestCase, isInstructionsOnly, onClose, onSave, popupData]);

    const handleCreateSuccess = useCallback((newTestCase) => {
        setInternalCreateOpen(false);
        if (!newTestCase?.id) return;
        setInternalSelectedTestCase(newTestCase.id);
        setInternalSearchTerm('');
        internalSearch('', 1, false);
    }, [internalSearch]);

    const selectedOption = effectiveOptions.find(
        (option) => (option?.id || option) === effectiveSelectedTestCase
    );
    const effectiveWarningNote =
        warningNote ||
        selectedOption?.automation_status === AutomationStatusEnum.NLP_AUTOMATED;

    const createOption =
        !legacyTestCaseId
            ? {
                id: CREATE_NEW_VALUE,
                isCreateOption: true,
                searchTerm: internalSearchTerm,
            }
            : null;
    const displayedOptions =
        createOption && !effectiveOptions.some((option) => option?.id === CREATE_NEW_VALUE)
            ? [createOption, ...effectiveOptions]
            : effectiveOptions;

    const buttons = [
        {
            label: 'Cancel',
            variant: 'outline',
            className: 'primary',
            onClick: onClose,
        },
        {
            label: copy.primaryButtonLabel,
            variant: 'primary',
            startIcon: <Cog6ToothIcon className="icon white" />,
            className: 'white bg-blue600',
            onClick: handleSave,
            disabled: !effectiveSelectedTestCase || effectiveSaving,
            loading: effectiveSaving,
        },
    ];

    const defaultGetOptionLabel = (option) => {
        if (!option) return '';
        if (option.isCreateOption) return option.searchTerm || '';
        if (option.isLoadingPlaceholder) return 'Loading...';
        if (typeof option === 'string') return option;
        const key = option.key || option.id || 'Unknown';
        const title = option.title || option.name || 'Untitled';
        return `${key} - ${title}`;
    };

    const defaultGetOptionValue = (option) => {
        if (option?.isCreateOption) return option.id;
        if (option?.isLoadingPlaceholder) return option.id;
        return option?.id || option;
    };

    const defaultRenderOption = (option, { selected }) => {
        if (option.isCreateOption) {
            const label =
                option.searchTerm && option.searchTerm.trim()
                    ? `+ Create Test Case "${option.searchTerm.trim()}"`
                    : '+ Create Test Case';
            return <div className={styles.createOption}>{label}</div>;
        }
        if (option.isLoadingPlaceholder) {
            return (
                <div className={styles.loadingOption} style={{ pointerEvents: 'none' }}>
                    <div className={styles.loadingSpinner} />
                    <span>Loading more test cases...</span>
                </div>
            );
        }
        return (
            <div className={styles.customOption}>
                <div className={styles.optionHeader}>
                    <span className={styles.optionTitle}>
                        {(getOptionLabel ?? defaultGetOptionLabel)(option)}
                        {option.automation_status === 'NLP_AUTOMATED' && (
                            <span className={styles.pill}>NLP Automated</span>
                        )}
                    </span>
                    {selected && <CheckCircle className={styles.selectedIcon} fontSize="small" />}
                </div>
                {option.module && (
                    <span className={styles.optionModule}>Module: {option.module.name}</span>
                )}
            </div>
        );
    };

    return (
        <>
            <ModalWrapper
                isOpen
                onClose={onClose}
                title={copy.modalTitle}
                footer={<CommonFooter buttons={buttons} align="right" />}
            >
                <div className={styles.saveExecutionRecordContent}>
                    <div className={styles.container}>
                        <span className={styles.title}>{copy.heading}</span>
                        <p className={styles.description}>{copy.description}</p>
                    </div>
                    <div className={styles.container}>
                        <AutoSuggest
                            name="testCase"
                            size="small"
                            value={effectiveSelectedTestCase}
                            disabled={disabled || Boolean(legacyTestCaseId)}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === CREATE_NEW_VALUE) {
                                    if (onCreateNew) onCreateNew();
                                    else setInternalCreateOpen(true);
                                    return;
                                }
                                if (onTestCaseChange) onTestCaseChange(value);
                                else {
                                    setInternalSelectedTestCase(value || null);
                                    setInternalApiError(null);
                                }
                            }}
                            options={displayedOptions}
                            loading={effectiveLoading}
                            noOptionsText={getNoOptionsText?.() ?? (effectiveApiError ? `Error: ${effectiveApiError}` : 'No options found')}
                            loadingText="Searching test cases..."
                            getOptionLabel={getOptionLabel ?? defaultGetOptionLabel}
                            getOptionValue={getOptionValue ?? defaultGetOptionValue}
                            isOptionEqualToValue={isOptionEqualToValue}
                            filterOptions={filterOptions ?? ((opts) => opts)}
                            renderOption={renderOption ?? defaultRenderOption}
                            onInputChange={onInputChange ?? handleInternalInputChange}
                            onDropdownScroll={onDropdownScroll ?? handleInternalDropdownScroll}
                            onKeyboardNavigation={onKeyboardNavigation ?? handleInternalKeyboardNavigation}
                            openOnFocus
                            required
                            error={effectiveApiError}
                            helperText={effectiveApiError ? `Error: ${effectiveApiError}` : undefined}
                            label="Search Test Case"
                        />
                    </div>
                    {effectiveWarningNote && (
                        <span className={styles.warningNote}>
                            {SAVE_EXECUTION_RECORD_OVERWRITE_WARNING}
                        </span>
                    )}
                </div>
            </ModalWrapper>
            <QuickCreateTestCaseModal
                isOpen={effectiveCreateModalOpen}
                onClose={onCreateModalClose ?? (() => setInternalCreateOpen(false))}
                onSuccess={handleCreateSuccess}
                initialTitle={internalSearchTerm}
                {...createModalProps}
            />
        </>
    );
}
